import { DateTime } from 'luxon';
import { v4 as uuid } from 'uuid';

import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import type { ChartOfAccount, ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import store from 'app-components/utils/redux-store';

import type { RecurrenceType } from './customFrequencyTypes';
import { FrequencyEnum } from './frequency';
import type { AutoAdjustAmount, STType, ScheduledTransaction } from './scheduledTransactionsTypes';
import { AutoAdjustAmountEnum, STTypeEnum, mkRecurrence, mkScheduledTransaction } from './scheduledTransactionsTypes';

export const frequencyAliasTypes = {
  everyWeekSimplifi: 'Every week',
  everyTwoWeeksSimplifi: 'Every two weeks',
  twiceAMonthSimplifi: 'Twice a month',
  everyMonthSimplifi: 'Every month',
  every2MonthsSimplifi: 'Every 2 months',
  everyQuarterSimplifi: 'Every quarter',
  every6MonthsSimplifi: 'Every 6 months',
  everyYearSimplifi: 'Every year',
  custom: 'Custom',
} as const;

export type FrequencyAlias = (typeof frequencyAliasTypes)[keyof typeof frequencyAliasTypes];

export const frequencyAliases = [
  frequencyAliasTypes.everyWeekSimplifi,
  frequencyAliasTypes.everyTwoWeeksSimplifi,
  frequencyAliasTypes.twiceAMonthSimplifi,
  frequencyAliasTypes.everyMonthSimplifi,
  frequencyAliasTypes.every2MonthsSimplifi,
  frequencyAliasTypes.everyQuarterSimplifi,
  frequencyAliasTypes.every6MonthsSimplifi,
  frequencyAliasTypes.everyYearSimplifi,
];

export const weekDays = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'] as const;

export const recurrenceFromFrequencyAlias = (frequencyAlias: FrequencyAlias, dueOn?: string) => {
  let recurrence: RecurrenceType | undefined;

  if (dueOn && frequencyAlias) {
    const date = DateTime.fromISO(dueOn);

    switch (frequencyAlias) {
      case frequencyAliasTypes.everyWeekSimplifi:
        {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: FrequencyEnum.WEEKLY,
            interval: 1,
            ...(day && {
              byDay: [day],
            }),
          };
        }
        break;
      case frequencyAliasTypes.everyTwoWeeksSimplifi:
        {
          const day = date && date.weekday && weekDays[date.weekday - 1];
          recurrence = {
            frequency: FrequencyEnum.WEEKLY,
            interval: 2,
            ...(day && {
              byDay: [day],
            }),
          };
        }
        break;
      case frequencyAliasTypes.twiceAMonthSimplifi:
        {
          const daysInMonth = 31;
          let day1 = date?.day;
          day1 = day1 > 28 ? day1 - daysInMonth - 1 : day1;
          const day2raw = date?.day + 15;
          let day2 = day2raw % daysInMonth;
          day2 = day2 && day2 <= 28 ? day2 : day2raw - daysInMonth - 1;
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 1,
            ...(day1 && day2
              ? {
                  byMonthDay: [day1, day2],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyMonthSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 1,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.every2MonthsSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 2,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyQuarterSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 3,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.every6MonthsSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 6,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      case frequencyAliasTypes.everyYearSimplifi:
        {
          const day =
            date && date.day && date.daysInMonth && (date.day < 29 ? date.day : date.day - date.daysInMonth - 1);
          recurrence = {
            frequency: FrequencyEnum.MONTHLY,
            interval: 12,
            ...(day
              ? {
                  byMonthDay: [day],
                }
              : undefined),
          };
        }
        break;
      default:
    }
  }

  return recurrence;
};

export const frequencyAliasFromRecurrence = (recurrence: any) => {
  let frequencyAlias: FrequencyAlias = frequencyAliasTypes.custom;

  if (recurrence && recurrence.frequency && recurrence.interval) {
    if (recurrence.frequency === FrequencyEnum.WEEKLY && recurrence.interval === 1) {
      frequencyAlias = frequencyAliasTypes.everyWeekSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.WEEKLY && recurrence.interval === 2) {
      frequencyAlias = frequencyAliasTypes.everyTwoWeeksSimplifi;
    } else if (
      recurrence.frequency === FrequencyEnum.MONTHLY &&
      recurrence.interval === 1 &&
      recurrence.byMonthDay &&
      recurrence.byMonthDay.length === 2
    ) {
      frequencyAlias = frequencyAliasTypes.twiceAMonthSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 1) {
      frequencyAlias = frequencyAliasTypes.everyMonthSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 6) {
      frequencyAlias = frequencyAliasTypes.every6MonthsSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 2) {
      frequencyAlias = frequencyAliasTypes.every2MonthsSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 3) {
      frequencyAlias = frequencyAliasTypes.everyQuarterSimplifi;
    } else if (recurrence.frequency === FrequencyEnum.MONTHLY && recurrence.interval === 12) {
      frequencyAlias = frequencyAliasTypes.everyYearSimplifi;
    } else {
      frequencyAlias = frequencyAliasTypes.custom;
    }
  }

  return frequencyAlias;
};

export const stTypes = [STTypeEnum.BILL, STTypeEnum.SUBSCRIPTION, STTypeEnum.INCOME, STTypeEnum.TRANSFER];

export const STTypeNamesEnum = Object.freeze({
  [STTypeEnum.EXPENSE]: 'Bill', // Expense
  [STTypeEnum.INCOME]: 'Income',
  [STTypeEnum.TRANSFER]: 'Transfer',
  [STTypeEnum.BILL]: 'Bill',
  [STTypeEnum.SUBSCRIPTION]: 'Subscription',
  [STTypeEnum.UNKNOWN]: 'Unknown',
});

export const typeFromScheduledTransaction = (scheduledTransaction: ScheduledTransaction): STType => {
  let stType: STType = STTypeEnum.UNKNOWN;

  if (scheduledTransaction.transaction?.isBill) {
    stType = STTypeEnum.BILL;
  } else if (scheduledTransaction.transaction?.isSubscription) {
    stType = STTypeEnum.SUBSCRIPTION;
  } else if (scheduledTransaction.transaction?.coa) {
    const { coa } = scheduledTransaction.transaction;
    stType = typeFromCOA(coa);
  }
  return stType;
};

export const typeFromCOA = (coa: ChartOfAccountProps): STType => {
  let stType: STType = STTypeEnum.UNKNOWN;

  if (coa) {
    if (coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isIncomeCat(null, coa.id)) {
      stType = STTypeEnum.INCOME;
    } else if (
      coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT ||
      coa.type === chartOfAccountsTypes.CoaTypeEnum.BALANCE_ADJUSTMENT
    ) {
      stType = STTypeEnum.TRANSFER;
    } else {
      stType = STTypeEnum.BILL;
    }
  }
  return stType;
};

export const negatorFromCOA = (coa: ChartOfAccount) => {
  let negator = -1;

  if (coa && coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY && categoriesSelectors.isIncomeCat(null, coa.id)) {
    negator = +1;
  } else {
    negator = -1;
  }

  return negator;
};

export const negatorFromType = (type: STType) => {
  let negator = -1;

  switch (type) {
    case STTypeEnum.BILL:
    case STTypeEnum.SUBSCRIPTION:
    case STTypeEnum.TRANSFER:
    case STTypeEnum.EXPENSE:
    case STTypeEnum.UNKNOWN:
      negator = -1;
      break;

    case STTypeEnum.INCOME:
      negator = +1;
      break;

    default:
      negator = -1;
  }

  return negator;
};

export const getSchedTxnType = (txn?: TransactionProps | null) => {
  if (txn?.isBill) {
    return 'bill';
  }
  if (txn?.isSubscription) {
    return 'subscription';
  }
  if (transactionsUtils.isSplitTxn(txn)) {
    return 'item';
  }
  if (txn?.coa?.type === 'ACCOUNT') {
    return 'transfer';
  }
  if (txn?.coa?.type === 'CATEGORY') {
    return categoriesSelectors.isIncomeCat(null, txn.coa.id) ? 'income' : 'bill';
  }
  return 'item';
};

export const getDisplayName = (txn: TransactionProps) => {
  const investmentAction =
    txn && txn.investmentTxnType && transactionsUtils?.InvestmentTxnActionsByType[txn.investmentTxnType];
  const investmentActionFormatted = investmentAction ? `${investmentAction} ` : '';
  const displayName = txn?.type === 'INVESTMENT' ? `${investmentActionFormatted}${txn?.symbol}` : txn?.payee;

  return displayName;
};

export const getDisplayNameFromAdjustAmount = (amountSetting?: AutoAdjustAmount) => {
  if (!amountSetting) return '';
  if (amountSetting === AutoAdjustAmountEnum.statement) {
    return 'Statement Balance';
  }
  if (amountSetting === AutoAdjustAmountEnum.minimum) {
    return 'Minimum Balance';
  }
  if (amountSetting === AutoAdjustAmountEnum.total) {
    return 'Total Balance';
  }
  if (amountSetting === AutoAdjustAmountEnum.average) {
    return 'Average Balance';
  }
  return '';
};

const getMLKDateISO = (year: number): string => {
  const januaryFirst = DateTime.local(year, 1, 1);
  const firstMondayOffset = (8 - januaryFirst.weekday) % 7;
  const thirdMonday = januaryFirst.plus({ days: firstMondayOffset + 14 });
  return thirdMonday.toISODate() || '';
};

const weekDaysOffset = [0, 0, 0, 0, 0, 2, 1];
export const taxDueDates = (
  year: number,
): { q0Due: string; q1Due: string; q2Due: string; q3Due: string; q4Due: string } => {
  const q0Due =
    DateTime.local(year, 1, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 1, 15).weekday - 1] })
      .toISODate() || '';
  const mlkDate0 = getMLKDateISO(year);
  const q1Due =
    DateTime.local(year, 4, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 4, 15).weekday - 1] })
      .toISODate() || '';
  const q2Due =
    DateTime.local(year, 6, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 6, 15).weekday - 1] })
      .toISODate() || '';
  const q3Due =
    DateTime.local(year, 9, 15)
      .plus({ days: weekDaysOffset[DateTime.local(year, 9, 15).weekday - 1] })
      .toISODate() || '';
  const q4Due =
    DateTime.local(year + 1, 1, 15)
      .plus({
        days: weekDaysOffset[DateTime.local(year + 1, 1, 15).weekday - 1],
      })
      .toISODate() || '';
  const mlkDate4 = getMLKDateISO(year + 1);

  return {
    q0Due: q0Due === mlkDate0 ? DateTime.fromISO(q0Due).plus({ days: 1 }).toISODate() || '' : q0Due,
    q1Due,
    q2Due,
    q3Due,
    q4Due: q4Due === mlkDate4 ? DateTime.fromISO(q4Due).plus({ days: 1 }).toISODate() || '' : q4Due,
  };
};

export const mkEstimatedTaxesModel = (year: number, accountId: string): ScheduledTransaction => {
  const dueDates = taxDueDates(year);
  const federalTaxCategory = store ? categoriesSelectors.getFederalTaxCategory(store.getState()) : undefined;
  return mkScheduledTransaction({
    clientId: uuid().toUpperCase(),
    dueOn: dueDates.q1Due,
    recurrence: mkRecurrence({
      interval: 1,
      frequency: FrequencyEnum.YEARLY,
      endOn: dueDates.q4Due,
      byDates: [
        DateTime.fromISO(dueDates.q1Due).toFormat('M/d'),
        DateTime.fromISO(dueDates.q2Due).toFormat('M/d'),
        DateTime.fromISO(dueDates.q3Due).toFormat('M/d'),
        DateTime.fromISO(dueDates.q4Due).toFormat('M/d'),
      ],
      alias: 'MULTIPLE_FIXED_DATES',
    }),
    transaction: {
      accountId: accountId,
      postedOn: dueDates.q1Due,
      payee: 'United States Treasury', // or 'IRS'?
      memo: `Tax Year ${year}`,
      coa: {
        type: federalTaxCategory?.id ? 'CATEGORY' : 'UNCATEGORIZED',
        id: federalTaxCategory?.id || '0',
      },
      amount: -100,
      state: 'PENDING',
      matchState: 'NOT_MATCHED',
      source: 'USER_ENTERED',
      type: 'CASH_FLOW',
    },
  });
};
